import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getSimulatorExercise,
  getSimulatorExerciseSchedule,
  getListStudentLedEnrollments,
  getSimulatorExercisePersonLastSession,
} from 'modules/simulatorTrainings/common/api/simulatorExerciseService'
import { GetSimulatorExercisePersonLastSessionModel } from 'modules/simulatorTrainings/common/types/student'
import {
  SimulatorTrainingStudentState,
  ApiSimulatorExerciseResponse,
  ApiSimulatorExerciseScheduleResponse,
  ApiListStudentLedEnrollmentsResponse,
  ApiGetStudentLedPersonLastSessionResponse,
  LoadExerciseDetailsDataParams,
} from './types'

const initialState: SimulatorTrainingStudentState = {
  exerciseDetailsData: {},
  scheduleDetailsData: {},
  listStudentLedEnrollmentsData: {},
  personLastSessionData: {},
}

const simulatorTrainingStudentSlice = createSlice({
  name: 'simulatorTrainingStudent',
  initialState,
  reducers: {
    exerciseDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSimulatorExerciseResponse>>
    ) {
      state.exerciseDetailsData = action.payload
    },
    scheduleDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSimulatorExerciseScheduleResponse>>
    ) {
      state.scheduleDetailsData = action.payload
    },
    clearDetailsData(state) {
      state.exerciseDetailsData = initialState.exerciseDetailsData
      state.scheduleDetailsData = initialState.scheduleDetailsData
    },
    listStudentLedEnrollmentsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListStudentLedEnrollmentsResponse>>
    ) {
      state.listStudentLedEnrollmentsData = action.payload
    },
    personLastSessionLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiGetStudentLedPersonLastSessionResponse>>
    ) {
      state.personLastSessionData = action.payload
    },
    clearPersonLastSession(state) {
      state.personLastSessionData = initialState.personLastSessionData
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  clearDetailsData,
  exerciseDetailsLoaded,
  scheduleDetailsLoaded,
  listStudentLedEnrollmentsLoaded,
  personLastSessionLoaded,
  clearPersonLastSession,
} = simulatorTrainingStudentSlice.actions
export { clearDetailsData, listStudentLedEnrollmentsLoaded, clearPersonLastSession }
export default simulatorTrainingStudentSlice.reducer

export const loadExerciseData = (exerciseId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getSimulatorExercise(exerciseId),
    },
    payloadAction: exerciseDetailsLoaded,
  })
}

export const handleLoadExerciseDetailsData =
  (
    response: ApiMiddlewarePayload<ApiSimulatorExerciseScheduleResponse>,
    params: LoadExerciseDetailsDataParams
  ): AppThunk =>
  async (dispatch, getState) => {
    const { error, result } = response
    const { payloadAction } = params
    if (!error && result) {
      const { id } = getState().simulatorTrainingStudent.exerciseDetailsData.result?.details || {}
      const { exerciseId } = result.details
      // TODO: Check ids, different ids from backend now
      id !== exerciseId && dispatch(loadExerciseData(exerciseId) as MiddlewareApiAction)
    }
    dispatch(payloadAction(response))
  }

export const loadExerciseScheduleData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getSimulatorExerciseSchedule(scheduleId),
    },
    payloadAction: scheduleDetailsLoaded,
    nextAction: {
      action: handleLoadExerciseDetailsData,
      params: { payloadAction: scheduleDetailsLoaded },
    },
  })
}

export const loadListStudentLedEnrollmentsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListStudentLedEnrollments(scheduleId),
    },
    payloadAction: listStudentLedEnrollmentsLoaded,
  })
}

export const getSimulatorExercisePersonLastSessionData = (
  params: GetSimulatorExercisePersonLastSessionModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      session: () => getSimulatorExercisePersonLastSession(params),
    },
    payloadAction: personLastSessionLoaded,
  })
}
